import React, { useContext, useEffect, useRef, useState } from 'react';
import { Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Location } from 'react-router';
import { PageHeader, Section, SpinnerArea } from '../../../../../../shared/components';
import OperationContext from '../../../context';
import CalculatorSectorContext from './CalculatorSectorContext';
import MainContext from '../../../../../App/MainProvider/context';
import RouteLeavingGuard from '../../../../../App/RouteLeavingGuard';
import OperationSubtitle from '../../../OperationSubtitle';
import useQuery from '../../../../../../shared/hooks/useQuery';
import SectorTabContainer from './SectorTabContainer';
import { SectorTabContainerBaseHandle } from '../Shared/SectorTabContainerBase';
import {
  calculateBuildingNotEstimatedEmissions,
  calculateEnergyConsumptionData,
  calculateEnergyEfficiency,
  calculateLandUseChange,
  calculateRawMaterial,
  calculateRoadMaintenance,
  calculateRoadPavement,
  calculateStreetLights,
  setBuildingEstimatedEmissions,
  setBuildingNotEstimatedEmissions,
} from '../../Utils/moduleCalculates';

const Calculator: React.FC = () => {
  const { operation, loading } = useContext(OperationContext);

  const {
    calculateId,
    landUseChange,
    energyConsumptionConstruction,
    energyConsumptionOperation,
    rawMaterialUse,
    streetlights,
    roadPavement,
    constructionEquipment,
    roadMaintenance,
    energyEfficiency,
    buildingConstruction,
    totalConstructionOverriden,
    totalOperationOverriden,
    isFinished,
  } = useContext(CalculatorSectorContext);
  const location = useLocation();
  const { t } = useTranslation();
  const { service } = useContext(MainContext);

  const sectorTabRef = useRef<SectorTabContainerBaseHandle>(null);

  // construction
  const [updatedLandUseChange, setUpdatedLandUseChange] = useState(landUseChange);
  const [updatedEnergyConsumptionConstruction, setUpdatedEnergyConsumptionConstruction] = useState(
    energyConsumptionConstruction
  );
  const [updatedBuildingConstruction, setUpdatedBuildingConstruction] =
    useState(buildingConstruction);
  const [updatedRoadPavement, setUpdatedRoadPavement] = useState(roadPavement);
  const [updatedConstructionEquipment, setUpdatedConstructionEquipment] =
    useState(constructionEquipment);
  const [updatedRawMaterial, setUpdatedRawMaterial] = useState(rawMaterialUse);

  // operation
  const [updatedStreetlights, setUpdatedStreetlights] = useState(streetlights);
  const [updatedRoadMaintenance, setUpdatedRoadMaintenance] = useState(roadMaintenance);
  const [updatedEnergyEfficiency, setUpdatedEnergyEfficiency] = useState(energyEfficiency);
  const [updatedEnergyConsumptionOperation, setUpdatedEnergyConsumptionOperation] = useState(
    energyConsumptionOperation
  );

  const { measurementSystem } = useContext(CalculatorSectorContext);
  const updateTotalCarbonRawMaterialUse = () => {
    const updatedMaterials = rawMaterialUse.rows.map((x) => {
      const totalCarbon = calculateRawMaterial(x.factor, x.quantity);
      return {
        ...x,
        totalCarbon,
      };
    });
    rawMaterialUse.rows = updatedMaterials;
    rawMaterialUse.subTotal =
      updatedMaterials.length > 0
        ? updatedMaterials.reduce((sum, row) => sum + row.totalCarbon, 0)
        : null;
    setUpdatedRawMaterial(rawMaterialUse);
  };

  const updateTotalCarbonBuildingConstruction = () => {
    if (buildingConstruction.isEstimated === false && buildingConstruction.totalArea) {
      const updatedConstruction = setBuildingNotEstimatedEmissions(
        buildingConstruction,
        measurementSystem.id
      );

      const result = calculateBuildingNotEstimatedEmissions(updatedConstruction);

      updatedConstruction.subTotal = result;
      setUpdatedBuildingConstruction(updatedConstruction);
    }

    if (buildingConstruction.isEstimated) {
      if (buildingConstruction.rows && buildingConstruction.rows.length > 0) {
        const totalArea = buildingConstruction.rows.reduce(
          (sum, row) => sum + row.defaultArea * row.numberBuildings,
          0
        );
        const newBuilding = setBuildingEstimatedEmissions(
          buildingConstruction,
          totalArea,
          measurementSystem.id
        );

        const totalCarbonSum = calculateBuildingNotEstimatedEmissions(newBuilding);

        newBuilding.subTotal = totalCarbonSum;
        setUpdatedBuildingConstruction(newBuilding);
      } else {
        buildingConstruction.subTotal = null;
        setUpdatedBuildingConstruction(buildingConstruction);
      }
    }
  };

  const updateTotalCarbonEnergyConsumptionConstruction = () => {
    const recalculatedRows = energyConsumptionConstruction.rows.map((x) => {
      const totalCarbon = 0;
      return {
        ...x,
        totalCarbon,
      };
    });

    const newRows = recalculatedRows.map((x) => ({
      ...x,
      x: calculateEnergyConsumptionData(x),
    }));

    const totalCarbonSum = newRows.reduce((sum, row) => sum + row.totalEmission, 0);

    energyConsumptionConstruction.subTotal = recalculatedRows.length > 0 ? totalCarbonSum : null;
    energyConsumptionConstruction.rows = newRows;

    setUpdatedEnergyConsumptionConstruction(energyConsumptionConstruction);
  };

  const updateTotalCarbonEnergyConsumptionOperation = () => {
    const recalculatedRows = energyConsumptionOperation.rows.map((x) => {
      const totalCarbon = 0;
      return {
        ...x,
        totalCarbon,
      };
    });

    const newRows = recalculatedRows.map((x) => ({
      ...x,
      x: calculateEnergyConsumptionData(x),
    }));

    const totalCarbonSum = newRows.reduce((sum, row) => sum + row.totalEmission, 0);
    energyConsumptionOperation.subTotal = recalculatedRows.length > 0 ? totalCarbonSum : null;
    energyConsumptionOperation.rows = newRows;
    setUpdatedEnergyConsumptionOperation(energyConsumptionOperation);
  };

  const updateTotalCarbonLandUseChange = () => {
    const recalculatedRows = landUseChange.rows.map((x) => {
      const totalCarbon = calculateLandUseChange(x.aboveGBCarbon, x.belowGBCarbon, x.area);
      return {
        ...x,
        totalCarbon,
      };
    });
    landUseChange.rows = recalculatedRows;
    const totalCarbonSum = recalculatedRows.reduce((sum, row) => sum + row.totalCarbon, 0);
    landUseChange.subTotal = recalculatedRows.length > 0 ? totalCarbonSum * (44 / 12) : null;
    setUpdatedLandUseChange(landUseChange);
  };

  const updateTotalConstruction = () => {
    if (constructionEquipment.isFuelData) {
      if (constructionEquipment.fuelDataRows && constructionEquipment.fuelDataRows.length > 0) {
        const totalFuelRows = constructionEquipment.fuelDataRows.reduce(
          (sum, row) => sum + row.totalEmission,
          0
        );

        constructionEquipment.subTotal = totalFuelRows;
        setUpdatedConstructionEquipment(constructionEquipment);
      }
    } else if (
      constructionEquipment.vehicleDataRows &&
      constructionEquipment.vehicleDataRows.length > 0
    ) {
      const updatedRows = constructionEquipment.vehicleDataRows;

      const totalCarbonSum = updatedRows.reduce((sum, row) => sum + row.totalEmission, 0);
      const totalCo2 = updatedRows.reduce((sum, row) => sum + row.co2Emission, 0);
      const totalCH4 = updatedRows.reduce(
        (sum, row) => sum + (row.ch4Emission !== null ? row.ch4Emission : 0),
        0
      );
      const totalN2O = updatedRows.reduce(
        (sum, row) => sum + (row.n2OEmission !== null ? row.n2OEmission : 0),
        0
      );

      constructionEquipment.co2Total = totalCo2;
      constructionEquipment.ch4Total = totalCH4;
      constructionEquipment.n2OTotal = totalN2O;
      constructionEquipment.subTotal = totalCarbonSum;

      setUpdatedConstructionEquipment(constructionEquipment);
    }
  };

  const updateTotalStreetlights = () => {
    const bulbs = Number(streetlights.numberOfBulbs);
    const wattage = Number(streetlights.wattage);
    const hours = Number(streetlights.hoursDay);

    if (bulbs && wattage && hours) {
      const emissionFactor =
        streetlights.isCustomFactor && streetlights.customEmissionFactor
          ? streetlights.customEmissionFactor
          : streetlights.defaultFactorValue;

      const result = calculateStreetLights(bulbs, wattage, hours, emissionFactor);

      streetlights.subTotal = result;
      setUpdatedStreetlights(streetlights);
    }
  };

  const updateTotalRoadPavement = () => {
    if (roadPavement.isTotalAreaKnown !== null) {
      if (roadPavement.isTotalAreaKnown === true && roadPavement.totalArea) {
        const factorValue =
          measurementSystem.id === 2
            ? roadPavement.factorValue
            : roadPavement.factorValue / roadPavement.sqFeetToSqMeters;
        const result = calculateRoadPavement(factorValue, roadPavement.totalArea);

        const areaCalculatedMetric =
          measurementSystem.id === 2
            ? roadPavement.totalArea / roadPavement.sqFeetToSqMeters
            : roadPavement.totalArea;
        updatedRoadMaintenance.areaFromPavement = areaCalculatedMetric;
        setUpdatedRoadMaintenance(updatedRoadMaintenance);

        roadPavement.subTotal = result;
      }

      if (
        roadPavement.isTotalAreaKnown === false &&
        roadPavement.pavedRoadLength &&
        roadPavement.numberOfLanes
      ) {
        const factor = roadPavement.factorValue / roadPavement.sqFeetToSqMeters;
        const resultingRoadlen = roadPavement.numberOfLanes * roadPavement.factorWidth;
        const pavedRoad = roadPavement.pavedRoadLength * 1000;
        const areaCalculated = resultingRoadlen * pavedRoad;

        const result = calculateRoadPavement(factor, areaCalculated);
        roadPavement.subTotal = result;

        updatedRoadMaintenance.areaFromPavement = areaCalculated;
        setUpdatedRoadMaintenance(updatedRoadMaintenance);
      }

      setUpdatedRoadPavement(roadPavement);
    }
  };

  const updateTotalRoadMaintenance = () => {
    const anyRoadMaintenance = roadMaintenance.area && roadMaintenance.area !== null;
    const anyRoadPavement = updatedRoadPavement.totalArea !== null;
    const different = updatedRoadPavement.totalArea !== roadMaintenance.area;

    if ((!anyRoadPavement && anyRoadMaintenance) || (anyRoadPavement && different)) {
      roadMaintenance.isCustomArea = true;
    } else {
      roadMaintenance.isCustomArea = false;
    }

    if (roadMaintenance.area !== null) {
      const result = calculateRoadMaintenance(roadMaintenance.factorValue, roadMaintenance.area);
      roadMaintenance.subTotal = result;
    }

    if (
      updatedRoadMaintenance.areaFromPavement &&
      roadMaintenance.areaFromPavement !== null &&
      roadMaintenance.isCustomArea !== true
    ) {
      const result = calculateRoadMaintenance(
        roadMaintenance.factorValue,
        updatedRoadMaintenance.areaFromPavement
      );
      roadMaintenance.subTotal = result;
    }

    setUpdatedRoadMaintenance(roadMaintenance);
  };

  const findWattageValueByCant = (value: number) => {
    const { factors } = energyEfficiency;
    for (let i = 0; i < factors.length; i += 1) {
      const current = factors[i];
      const next = factors[i + 1];

      if (value >= current.conventionalUnit && (!next || value < next.conventionalUnit)) {
        return current.cflUnit;
      }
    }
    return null;
  };

  const updateTotalEnergyEfficiency = () => {
    const units = Number(energyEfficiency.unitsReplaced);
    const wattage = Number(energyEfficiency.wattageConventional);
    const hours = Number(energyEfficiency.hoursDay);

    energyEfficiency.defaultUnit = wattage ? findWattageValueByCant(wattage) : null;

    if (units && wattage && hours) {
      let wattsValue = null;
      if (energyEfficiency.isCustomWattage) {
        wattsValue = energyEfficiency.customWattageReplacement;
      } else {
        wattsValue = energyEfficiency.defaultUnit;
      }

      const result = calculateEnergyEfficiency(
        units,
        wattage,
        Number(wattsValue),
        hours,
        energyEfficiency.countryFactor
      );

      energyEfficiency.defaultInputValue = wattsValue;
      energyEfficiency.details = result;
      energyEfficiency.subTotal = result.avoidedEmissions;
    }
    if (energyEfficiency.isCustomWattage) {
      energyEfficiency.defaultInputValue = energyEfficiency.customWattageReplacement;
    }

    setUpdatedEnergyEfficiency(energyEfficiency);
  };

  useEffect(() => {
    if (rawMaterialUse.rows && rawMaterialUse.rows.length > 0) {
      updateTotalCarbonRawMaterialUse();
    } else {
      rawMaterialUse.rows = [];
      setUpdatedRawMaterial(rawMaterialUse);
    }

    if (landUseChange.rows && landUseChange.rows.length > 0) {
      updateTotalCarbonLandUseChange();
    } else {
      landUseChange.rows = [];
      setUpdatedLandUseChange(landUseChange);
    }

    if (energyConsumptionConstruction.rows && energyConsumptionConstruction.rows.length > 0) {
      updateTotalCarbonEnergyConsumptionConstruction();
    } else {
      energyConsumptionConstruction.rows = [];
      setUpdatedEnergyConsumptionConstruction(energyConsumptionConstruction);
    }

    if (energyConsumptionOperation.rows && energyConsumptionOperation.rows.length > 0) {
      updateTotalCarbonEnergyConsumptionOperation();
    } else {
      energyConsumptionOperation.rows = [];
      setUpdatedEnergyConsumptionOperation(energyConsumptionOperation);
    }

    if (buildingConstruction) {
      updateTotalCarbonBuildingConstruction();
    }

    if (streetlights && streetlights.subTotal) {
      updateTotalStreetlights();
    }

    if (roadPavement) {
      updateTotalRoadPavement();
    }

    if (constructionEquipment) {
      updateTotalConstruction();
    }
    if (roadMaintenance.areaFromPavement || roadMaintenance.area) {
      updateTotalRoadMaintenance();
    }

    if (energyEfficiency) {
      updateTotalEnergyEfficiency();
    }
  }, []);

  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [localTotalConstructionOverriden, setTotalConstructionOverriden] = useState<number | null>(
    totalConstructionOverriden
  );
  const [localTotalOperationOverriden, setTotalOperationOverriden] = useState<number | null>(
    totalOperationOverriden
  );

  useEffect(() => {
    setTotalConstructionOverriden(totalConstructionOverriden);
    setTotalOperationOverriden(totalOperationOverriden);
  }, [totalConstructionOverriden, totalOperationOverriden]);

  const queryParams = useQuery();
  const from = queryParams.get('from') || undefined;
  const title = `Operation ${operation.number} / GHG Calculator`;

  const navigate = useNavigate();

  const saveData = (targetLocation?: Location | null) => {
    const callback = () => {
      setIsSaveDisabled(true);
      if (targetLocation) {
        navigate(targetLocation);
      }
    };

    service(() => sectorTabRef.current!.saveChanges(), { callback });
  };

  return (
    <>
      <RouteLeavingGuard
        when={!isSaveDisabled}
        shouldBlockNavigation={() => !isSaveDisabled}
        from={location}
        onSave={(targetLocation) => saveData(targetLocation)}
        message={t('dirtyIndicatorsPopupMsg')}
      />
      <Container className="rolesAndPermissions body-calculator-color">
        <PageHeader title={title} backUrl={from}>
          <OperationSubtitle />
        </PageHeader>
        <SpinnerArea loading={loading}>
          <Row>
            <Section>
              <SectorTabContainer
                ref={sectorTabRef}
                calculatorId={calculateId != null ? calculateId : 0}
                operation={operation}
                landUseChange={updatedLandUseChange}
                energyConsumptionConstruction={updatedEnergyConsumptionConstruction}
                energyConsumptionOperation={updatedEnergyConsumptionOperation}
                constructionEquipment={updatedConstructionEquipment}
                rawMaterialUse={updatedRawMaterial}
                streetlights={updatedStreetlights}
                roadPavement={updatedRoadPavement}
                roadMaintenance={updatedRoadMaintenance}
                energyEfficiency={updatedEnergyEfficiency}
                buildingConstruction={updatedBuildingConstruction}
                loading={loading}
                isSaveDisabled={isSaveDisabled}
                setIsSaveDisabled={setIsSaveDisabled}
                totalConstructionOverriden={localTotalConstructionOverriden}
                setTotalConstructionOverriden={setTotalConstructionOverriden}
                totalOperationOverriden={localTotalOperationOverriden}
                setTotalOperationOverriden={setTotalOperationOverriden}
                isFinished={isFinished}
              />
            </Section>
          </Row>
        </SpinnerArea>
      </Container>
    </>
  );
};

export default Calculator;
